/**
 * In-memory mock backend used when VITE_API_BASE_URL is unset.
 *
 * Lets the SPA boot end-to-end on a developer's laptop or in a partner
 * walk-through demo without any AWS / RDS / Cirium dependency. The mock
 * preserves session state across navigations via localStorage, and
 * simulates the magic-link round trip by storing the verifier token
 * locally and routing the user straight back through `/auth/verify`.
 */

import type { FlightContext, PassDesign, ServicePayload } from '@insurwiz/wallet-core';
import { validatePassDesign } from '@insurwiz/wallet-core';

import { composeSeed, normalizeLegacyTravelDesign } from '../lib/designSeed';
import { NOTIFICATION_DEFAULTS } from '../lib/notificationDefaults';
import type {
  DemoPartner,
  DemoPass,
  DesignImageUploadUrlBody,
  DesignImageUploadUrlResponse,
  FlightLookup,
  MintPassRequest,
  NotificationSettings,
  PassDesignDocDto,
  PassDesignPutBody,
  PassDesignSummaryDto,
  PassTemplateKind,
} from '../types';
import type { AppleStyle } from '../walletStyles';

// NOTE: client.ts imports this module (mock dispatch) and we import its
// ApiClientError — a benign cycle: both sides only reference the other's
// exports inside functions, never at module-eval time. Throwing the REAL
// ApiClientError matters because the UI branches on `instanceof` (e.g.
// useDesignDoc's 422 handler, useFlightLookup's FLIGHT_NOT_FOUND branch) —
// an `Object.assign(new Error(), …)` lookalike never enters those branches.
import { ApiClientError } from './client';

export { generalizeAssistanceSeed } from '../lib/designSeed';

const STORE_KEY = 'walletDemo.mock';

interface MockStore {
  partner: DemoPartner | null;
  pendingMagicLinkToken: string | null;
  passes: DemoPass[];
  designs: StoredDesign[];
}

function load(): MockStore {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as MockStore;
      // Guard a store persisted before the pass-designer sub-project shipped.
      return { ...parsed, designs: parsed.designs ?? [] };
    }
  } catch {
    /* ignore */
  }
  return { partner: null, pendingMagicLinkToken: null, passes: [], designs: [] };
}

function save(store: MockStore): void {
  // Mirror `load()`'s resilience: in a node-env test (no `localStorage`) or a
  // browser with storage disabled/full, persisting is best-effort — the
  // in-memory pass is still returned to the caller.
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

const FAKE_FLIGHTS: Record<string, Omit<FlightLookup, 'iata'>> = {
  LH400: {
    scheduledDepartureUtc: '2026-05-12T13:25:00Z',
    scheduledArrivalUtc: '2026-05-12T16:30:00Z',
    originIata: 'FRA',
    originName: 'Frankfurt am Main',
    destinationIata: 'JFK',
    destinationName: 'New York JFK',
    airline: 'Lufthansa',
    monitorable: true,
  },
  W6201: {
    scheduledDepartureUtc: '2026-05-12T08:15:00Z',
    scheduledArrivalUtc: '2026-05-12T10:40:00Z',
    originIata: 'BUD',
    originName: 'Budapest Liszt Ferenc',
    destinationIata: 'LTN',
    destinationName: 'London Luton',
    airline: 'Wizz Air',
    monitorable: true,
  },
  BA487: {
    scheduledDepartureUtc: '2026-05-12T09:50:00Z',
    scheduledArrivalUtc: '2026-05-12T12:35:00Z',
    originIata: 'LHR',
    originName: 'London Heathrow',
    destinationIata: 'BCN',
    destinationName: 'Barcelona El Prat',
    airline: 'British Airways',
    monitorable: true,
  },
};

function delay(ms = 350): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mock only: the sign-in path the last `requestMagicLink` issued. There is no
 * e-mail in mock mode — the sign-in card offers this as a button.
 */
export function pendingSignInPath(): string | null {
  const token = load().pendingMagicLinkToken;
  return token ? `/auth/verify?token=${encodeURIComponent(token)}` : null;
}

export async function requestMagicLink(email: string): Promise<{ ok: true }> {
  await delay();
  const store = load();
  const token = `mock-${Math.random().toString(36).slice(2, 14)}`;
  store.pendingMagicLinkToken = token;
  // A different e-mail is a different account — as on the real API. (The
  // mock used to keep the first partner, so a second sign-in showed the
  // first partner's e-mail, passes and designs.)
  if (store.partner && store.partner.email !== email) {
    store.partner = null;
    store.passes = [];
    store.designs = [];
  }
  if (!store.partner) {
    store.partner = {
      id: `demo-${Math.random().toString(36).slice(2, 10)}`,
      email,
      displayName: null,
      companyName: null,
      primaryColor: '#0F766E',
      logoUrl: null,
      delayThresholdMinutes: 90,
      assistancePhone: null,
      loungeInfoText: null,
      esimLinkUrl: null,
      defaultPassClassType: 'generic',
      enabledTemplates: [
        'travel',
        'insurance',
        'motor_kotelezo',
        'motor_casco',
        'assistance',
        'life_accident',
      ],
      createdAt: new Date().toISOString(),
    };
  }
  save(store);
  // In mock mode we surface the magic-link target so the dev can click it
  // without checking an inbox.
  // eslint-disable-next-line no-console -- dev-only mock helper, mock mode only
  console.info(
    `[mock] Magic link issued. Open: ${window.location.origin}/auth/verify?token=${token}`,
  );
  return { ok: true };
}

export async function verifyMagicLink(
  token: string,
): Promise<{ jwt: string; partner: DemoPartner }> {
  await delay();
  const store = load();
  if (!store.pendingMagicLinkToken || store.pendingMagicLinkToken !== token) {
    throw new ApiClientError(400, {
      code: 'INVALID_TOKEN',
      message: 'Érvénytelen vagy lejárt link',
    });
  }
  store.pendingMagicLinkToken = null;
  save(store);
  return { jwt: `mock-jwt.${store.partner!.id}`, partner: store.partner! };
}

export async function getMe(): Promise<DemoPartner> {
  await delay(150);
  const store = load();
  if (!store.partner) throw new Error('No session');
  return store.partner;
}

export async function lookupFlight(iata: string, date: string): Promise<FlightLookup> {
  await delay(500);
  const normalized = iata.replace(/\s+/g, '').toUpperCase();
  const data = FAKE_FLIGHTS[normalized];
  if (!data) {
    throw new ApiClientError(404, {
      code: 'FLIGHT_NOT_FOUND',
      message: `Nem találtuk a ${normalized} járatot a ${date} napra. Próbáld: LH400, W6201, BA487.`,
    });
  }
  // The fixtures carry one sample day; move the schedule onto the requested
  // date (same wall times) so the pass says the day the operator picked.
  const onDate = (iso: string): string =>
    /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}${iso.slice(10)}` : iso;
  return {
    iata: normalized,
    ...data,
    scheduledDepartureUtc: onDate(data.scheduledDepartureUtc),
    scheduledArrivalUtc: onDate(data.scheduledArrivalUtc),
  };
}

export async function listPasses(): Promise<DemoPass[]> {
  await delay(200);
  const store = load();
  return [...store.passes].sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}

export async function getPass(id: string): Promise<DemoPass> {
  await delay(150);
  const store = load();
  const found = store.passes.find((p) => p.id === id);
  // Same answer as the API — the pass page branches on a 404.
  if (!found) throw new ApiClientError(404, { code: 'NOT_FOUND', message: 'Pass not found' });
  return found;
}

export async function mintPass(req: MintPassRequest): Promise<DemoPass> {
  await delay(900);
  const store = load();
  const isTravel = (req.templateKind ?? 'travel') === 'travel';
  // Travel: do a flight lookup; non-travel: skip the lookup entirely.
  const flight =
    isTravel && req.flightIata && req.flightDate
      ? await lookupFlight(req.flightIata, req.flightDate)
      : null;
  const expiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
  const pass: DemoPass = {
    id: `pass-${Math.random().toString(36).slice(2, 10)}`,
    recipientName: req.recipientName,
    recipientEmail: req.recipientEmail,
    flightIata: flight?.iata ?? null,
    flightDate: req.flightDate ?? null,
    originIata: flight?.originIata ?? null,
    destinationIata: flight?.destinationIata ?? null,
    scheduledDepartureUtc: flight?.scheduledDepartureUtc ?? null,
    scheduledArrivalUtc: flight?.scheduledArrivalUtc ?? null,
    templateKind: req.templateKind ?? 'travel',
    servicePayload: req.servicePayload ?? null,
    passClassType: req.classType ?? store.partner?.defaultPassClassType ?? 'generic',
    googleSaveUrl:
      req.offeredPlatforms == null || req.offeredPlatforms.includes('google')
        ? `https://pay.google.com/gp/v/save/mock-${Math.random().toString(36).slice(2)}`
        : null,
    appleSaveUrl: req.offeredPlatforms?.includes('apple')
      ? `https://wallet-demo-api.insurwiz.io/v1/portal/passes/mock-${Math.random().toString(36).slice(2)}.pkpass`
      : null,
    status: 'active',
    // Parity with the real backend: liveStatus rows exist only for
    // flight-bound (travel) passes — fabricating one for a policy pass made
    // the UI render flight state on e.g. an assistance pass in mock mode only.
    liveStatus: flight
      ? {
          state: 'on_time',
          delayMinutes: 0,
          gate: null,
          terminal: null,
          actualDeparture: null,
          actualArrival: null,
          updatedAt: new Date().toISOString(),
        }
      : null,
    expiresAt,
    createdAt: new Date().toISOString(),
  };
  store.passes.push(pass);
  save(store);
  return pass;
}

export async function deletePass(id: string): Promise<void> {
  await delay(150);
  const store = load();
  store.passes = store.passes.filter((p) => p.id !== id);
  save(store);
}

// ─── Pass Designer (sub-project 2) ────────────────────────────────────────────

interface StoredDesign {
  templateKind: PassTemplateKind;
  appleStyle: AppleStyle;
  design: PassDesign;
  isActive: boolean;
  /** Push-editor settings (spec 2026-08-31 Part B). Absent/null ⇒ built-in. */
  notifications?: NotificationSettings | null;
  updatedAt: string;
}

// ── GET-one seed: the REAL serviceToDesign over mirrored sample fixtures ─────
// The seed is built by CALLING serviceToDesign (never hand-rolled): relevance,
// semantics.originalDepartureDate, the live-trio topology and the ABSENCE of a
// barcode all come from the one shared implementation, so the mock seed cannot
// drift from the server's GET-one 404 → fresh-seed path. Only the sample
// FIXTURES are mirrored (the frontend must not import server code) — keep them
// in sync with apps/partner-portal-api/src/services/passDesignSamples.ts.

/** One fixed representative flight — LH 1414 BUD→FRA (= the API's SAMPLE_FLIGHT). */
export const SAMPLE_FLIGHT: FlightContext = {
  carrier: 'LH',
  number: '1414',
  departureAirport: 'BUD',
  arrivalAirport: 'FRA',
  scheduledDeparture: '2026-09-01T08:00:00Z',
  scheduledArrival: '2026-09-01T10:00:00Z',
  actualDeparture: null,
  actualArrival: null,
  status: 'ON_TIME',
  delayMinutes: 0,
  gate: null,
  terminal: null,
};

/**
 * Per-template sample ServicePayload (= the API's sampleServiceFor branches for
 * the two DEMO template kinds). The Apple/Google studios only mount 'travel'
 * and 'assistance'; the four policy kinds are unreachable from the designer UI
 * and fall back to the travel sample here.
 */
export function sampleServiceFor(templateKind: PassTemplateKind): ServicePayload {
  if (templateKind === 'assistance') {
    // Általános biztosítás template — the lounge kind is the render vehicle,
    // but the copy must read general-insurance, not lounge-specific.
    return {
      kind: 'lounge',
      loungeName: '24/7 Általános Asszisztencia',
      loungeCode: 'IW-DEMO-ASSZ-01',
      accessWindow: { from: '2026-09-01T00:00:00Z', to: '2027-09-01T00:00:00Z' },
    };
  }
  return {
    kind: 'lounge',
    loungeName: 'Menzies Aspire Lounge',
    loungeCode: 'IW-DEMO-LNG-01',
    accessWindow: { from: '2026-09-01T06:00:00Z', to: '2026-09-01T09:00:00Z' },
  };
}

/**
 * The GET-one fresh seed for (kind, style) — serviceToDesign over the sample
 * fixtures, de-lounged for the GENERAL template and framed as Hungarian
 * travel insurance (sample passenger) for TRAVEL (= the server's
 * handlePassDesignGet miss path). The single seed composition the mock GET
 * and the [LIVE]-lock sampleSeedFor both call, so they can never drift.
 */
export function designSeedFor(templateKind: PassTemplateKind, appleStyle: AppleStyle): PassDesign {
  return composeSeed(templateKind, appleStyle, sampleServiceFor(templateKind), SAMPLE_FLIGHT);
}

export async function listPassDesigns(): Promise<PassDesignSummaryDto[]> {
  await delay(150);
  const store = load();
  return store.designs
    .map((d) => ({
      templateKind: d.templateKind,
      appleStyle: d.appleStyle,
      isActive: d.isActive,
      updatedAt: d.updatedAt,
    }))
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
}

export async function getPassDesign(
  templateKind: PassTemplateKind,
  appleStyle: AppleStyle,
): Promise<PassDesignDocDto> {
  await delay(150);
  const store = load();
  const found = store.designs.find(
    (d) => d.templateKind === templateKind && d.appleStyle === appleStyle,
  );
  if (found) {
    // Same heal-on-load as the server (normalizeSavedDesign): a travel design
    // saved from the old lounge/English seed reads Hungarian travel insurance.
    const design =
      templateKind === 'travel' ? normalizeLegacyTravelDesign(found.design) : found.design;
    return {
      templateKind,
      appleStyle,
      isActive: found.isActive,
      updatedAt: found.updatedAt,
      seeded: false,
      design,
      // Same warn echo as handlePassDesignGet — non-blocking findings ride
      // along so the editor renders them on first load, mock mode included.
      warnings: validatePassDesign(design).filter((e) => e.severity === 'warn'),
      notifications: found.notifications ?? null,
      notificationDefaults: NOTIFICATION_DEFAULTS,
    };
  }
  const seed = designSeedFor(templateKind, appleStyle);
  return {
    templateKind,
    appleStyle,
    isActive: false,
    updatedAt: null,
    seeded: true,
    design: seed,
    warnings: validatePassDesign(seed).filter((e) => e.severity === 'warn'),
    notifications: null,
    notificationDefaults: NOTIFICATION_DEFAULTS,
  };
}

export async function putPassDesign(
  templateKind: PassTemplateKind,
  appleStyle: AppleStyle,
  body: PassDesignPutBody,
): Promise<PassDesignDocDto> {
  await delay(200);
  // ── Same gate as production (mirrors partner-portal-api handlePassDesignPut) ──
  // 1) STYLE_MISMATCH: the design.style must match the requested :appleStyle.
  if (body.design.style !== appleStyle) {
    throw new ApiClientError(400, {
      code: 'STYLE_MISMATCH',
      message: 'design.style must match the :appleStyle path segment',
    });
  }
  // 2) The REAL wallet-core validator — any `severity:'error'` blocks with 422;
  //    warns are echoed back on the 200 so every offline/page-mount test runs
  //    against the identical gate as the server. The throw is the REAL
  //    ApiClientError: useDesignDoc's 422 branch is an `instanceof` check.
  const validation = validatePassDesign(body.design);
  if (validation.some((e) => e.severity === 'error')) {
    throw new ApiClientError(422, {
      code: 'DESIGN_INVALID',
      message: 'Pass design failed Apple constraints',
      errors: validation,
    });
  }
  const warnings = validation.filter((e) => e.severity === 'warn');
  const store = load();
  const idx = store.designs.findIndex(
    (d) => d.templateKind === templateKind && d.appleStyle === appleStyle,
  );
  const updatedAt = new Date().toISOString();
  // COALESCE($5, …) parity: omitted isActive preserves the existing flag on
  // update and defaults to TRUE on first insert.
  const nextActive = body.isActive ?? (idx >= 0 ? store.designs[idx]!.isActive : true);
  // Tri-state notifications parity with the repo: omitted ⇒ keep the stored
  // value, null ⇒ clear (built-in behaviour), a document ⇒ store it.
  const nextNotifications =
    body.notifications !== undefined
      ? body.notifications
      : idx >= 0
        ? (store.designs[idx]!.notifications ?? null)
        : null;
  const record: StoredDesign = {
    templateKind,
    appleStyle,
    design: body.design,
    isActive: nextActive,
    notifications: nextNotifications,
    updatedAt,
  };
  if (idx >= 0) store.designs[idx] = record;
  else store.designs.push(record);
  save(store);
  return {
    templateKind,
    appleStyle,
    isActive: nextActive,
    updatedAt,
    seeded: false,
    design: body.design,
    warnings,
    notifications: nextNotifications,
    notificationDefaults: NOTIFICATION_DEFAULTS,
  };
}

export async function requestDesignImageUploadUrl(
  body: DesignImageUploadUrlBody,
): Promise<DesignImageUploadUrlResponse> {
  await delay(150);
  const scale = body.scale ?? '';
  const base = `designs/mock/${body.slot}`;
  return {
    uploadUrl: `mock://upload?slot=${encodeURIComponent(body.slot)}&scale=${encodeURIComponent(scale)}`,
    s3KeyBase: base,
    s3Key: `${base}${scale}.png`,
  };
}
